import React, { useEffect, useMemo, useState } from "react";

import api from "../api/api";
import MagmaSelect from "../components/MagmaSelect";
import "./Management.css";

const roleOptions = [
  { value: "1", label: "Administrador" },
  { value: "2", label: "Usuário" },
];

const roleLabelById = roleOptions.reduce((acc, role) => {
  acc[Number(role.value)] = role.label;
  return acc;
}, {});

function toErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map(item => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") return item.msg || JSON.stringify(item);
        return String(item);
      })
      .filter(Boolean)
      .join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return fallback;
}

export default function UsuariosPage() {
  const [items, setItems] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [userLinksMap, setUserLinksMap] = useState({});
  const [selectedLinkUserId, setSelectedLinkUserId] = useState("");
  const [linkForm, setLinkForm] = useState({ workspace_id: "", role_id: "2" });
  const [form, setForm] = useState({ nome: "", email: "", password: "", tipo_id: "1", ativo: true });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const workspaceNameById = useMemo(
    () =>
      workspaces.reduce((acc, item) => {
        acc[item.id] = item.nome;
        return acc;
      }, {}),
    [workspaces]
  );

  const selectedUserLinks = selectedLinkUserId ? (userLinksMap[selectedLinkUserId] || []) : [];

  const loadWorkspaces = async () => {
    const res = await api.get("/workspaces/");
    const list = res.data || [];
    setWorkspaces(list);
    if (!linkForm.workspace_id && list.length > 0) {
      setLinkForm(prev => ({ ...prev, workspace_id: String(list[0].id) }));
    }
  };

  const loadLinksByUser = async userId => {
    const res = await api.get(`/user-workspaces/users/${userId}`);
    setUserLinksMap(prev => ({
      ...prev,
      [String(userId)]: res.data || [],
    }));
  };

  const loadUsers = async () => {
    const res = await api.get("/users/");
    const users = res.data || [];
    setItems(users);

    const linksResults = await Promise.all(
      users.map(async user => {
        try {
          const linksRes = await api.get(`/user-workspaces/users/${user.id}`);
          return [String(user.id), linksRes.data || []];
        } catch (_err) {
          return [String(user.id), []];
        }
      })
    );

    const map = {};
    linksResults.forEach(([userId, links]) => {
      map[userId] = links;
    });
    setUserLinksMap(map);

    if (!selectedLinkUserId && users.length > 0) {
      setSelectedLinkUserId(String(users[0].id));
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError("");
        await Promise.all([loadUsers(), loadWorkspaces()]);
      } catch (err) {
        setError(toErrorMessage(err, "Falha ao carregar usuários."));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearForm = () => {
    setForm({ nome: "", email: "", password: "", tipo_id: "1", ativo: true });
    setEditingId(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");

      if (editingId) {
        const payload = {
          nome: form.nome,
          email: form.email,
          tipo_id: Number(form.tipo_id),
          ativo: Boolean(form.ativo),
        };
        if (form.password) {
          payload.password = form.password;
        }
        await api.put(`/users/${editingId}`, payload);
      } else {
        await api.post("/users/", {
          nome: form.nome,
          email: form.email,
          password: form.password,
          tipo_id: Number(form.tipo_id),
        });
      }

      await loadUsers();
      clearForm();
    } catch (err) {
      setError(toErrorMessage(err, "Erro ao salvar usuário."));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = user => {
    setEditingId(user.id);
    setSelectedLinkUserId(String(user.id));
    setForm({
      nome: user.nome || "",
      email: user.email || "",
      password: "",
      tipo_id: String(user.tipo_id || 1),
      ativo: Boolean(user.ativo),
    });
  };

  const handleDelete = async id => {
    if (!window.confirm("Deseja excluir este usuário?")) {
      return;
    }
    try {
      setLoading(true);
      setError("");
      await api.delete(`/users/${id}`);
      await loadUsers();
      if (String(editingId) === String(id)) {
        clearForm();
      }
      if (String(selectedLinkUserId) === String(id)) {
        setSelectedLinkUserId("");
      }
    } catch (err) {
      setError(toErrorMessage(err, "Erro ao excluir usuário."));
    } finally {
      setLoading(false);
    }
  };

  const handleLinkWorkspace = async () => {
    if (!selectedLinkUserId || !linkForm.workspace_id) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      await api.post(`/user-workspaces/workspaces/${linkForm.workspace_id}`, {
        user_id: Number(selectedLinkUserId),
        role_id: Number(linkForm.role_id),
      });
      await loadLinksByUser(selectedLinkUserId);
    } catch (err) {
      setError(toErrorMessage(err, "Erro ao vincular workspace ao usuário."));
    } finally {
      setLoading(false);
    }
  };

  const handleUnlinkWorkspace = async linkId => {
    if (!window.confirm("Remover este vínculo?")) {
      return;
    }
    try {
      setLoading(true);
      setError("");
      await api.delete(`/user-workspaces/${linkId}`);
      await loadLinksByUser(selectedLinkUserId);
    } catch (err) {
      setError(toErrorMessage(err, "Erro ao remover vínculo."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mgmt-page">
      <header className="mgmt-header">
        <div>
          <p className="mgmt-kicker">Admin</p>
          <h1 className="mgmt-title">Gestão de usuários</h1>
        </div>
      </header>

      <section className="mgmt-card">
        <h2 className="mgmt-section-title">{editingId ? "Editar usuário" : "Novo usuário"}</h2>
        <form className="mgmt-form" onSubmit={handleSubmit}>
          <label className="mgmt-label" htmlFor="usuario-nome">Nome</label>
          <input id="usuario-nome" className="mgmt-input" placeholder="Nome" value={form.nome} onChange={e => setForm(prev => ({ ...prev, nome: e.target.value }))} required />
          <label className="mgmt-label" htmlFor="usuario-email">E-mail</label>
          <input id="usuario-email" className="mgmt-input" type="email" placeholder="E-mail" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} required />
          <label className="mgmt-label" htmlFor="usuario-senha">{editingId ? "Nova senha (opcional)" : "Senha"}</label>
          <input id="usuario-senha" className="mgmt-input" type="password" placeholder={editingId ? "Nova senha (opcional)" : "Senha"} value={form.password} onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))} required={!editingId} />
          <label className="mgmt-label" htmlFor="usuario-tipo">Tipo de usuário</label>
          <MagmaSelect id="usuario-tipo" className="mgmt-input" value={form.tipo_id} onChange={e => setForm(prev => ({ ...prev, tipo_id: e.target.value }))}>
            <option value="1">Global</option>
            <option value="2">Workspace</option>
          </MagmaSelect>
          {editingId ? (
            <>
              <label className="mgmt-label" htmlFor="usuario-ativo">Status</label>
                <label className="mgmt-check" id="usuario-ativo">
                  <input type="checkbox" checked={form.ativo} onChange={e => setForm(prev => ({ ...prev, ativo: e.target.checked }))} />
                Usuário ativo
              </label>
            </>
          ) : null}
          <div className="mgmt-actions">
            <button className="mgmt-button" type="submit" disabled={loading}>{editingId ? "Salvar" : "Criar"}</button>
            {editingId ? <button className="mgmt-button ghost" type="button" onClick={clearForm}>Cancelar</button> : null}
          </div>
        </form>
      </section>

      <section className="mgmt-card">
        <h2 className="mgmt-section-title">Associar workspaces a usuário</h2>
        <div className="mgmt-form">
          <label className="mgmt-label" htmlFor="vinculo-usuario">Usuário</label>
          <MagmaSelect id="vinculo-usuario" className="mgmt-input" value={selectedLinkUserId} onChange={e => setSelectedLinkUserId(e.target.value)}>
            <option value="">Selecione usuário</option>
            {items.map(user => (
              <option key={user.id} value={user.id}>{user.nome} ({user.email})</option>
            ))}
          </MagmaSelect>
          <label className="mgmt-label" htmlFor="vinculo-workspace">Workspace</label>
          <MagmaSelect id="vinculo-workspace" className="mgmt-input" value={linkForm.workspace_id} onChange={e => setLinkForm(prev => ({ ...prev, workspace_id: e.target.value }))}>
            <option value="">Selecione workspace</option>
            {workspaces.map(ws => (
              <option key={ws.id} value={ws.id}>{ws.nome}</option>
            ))}
          </MagmaSelect>
          <label className="mgmt-label" htmlFor="vinculo-papel">Papel no workspace</label>
          <MagmaSelect id="vinculo-papel" className="mgmt-input" value={linkForm.role_id} onChange={e => setLinkForm(prev => ({ ...prev, role_id: e.target.value }))}>
            {roleOptions.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </MagmaSelect>
          <div className="mgmt-actions">
            <button className="mgmt-button" type="button" disabled={!selectedLinkUserId || !linkForm.workspace_id || loading} onClick={handleLinkWorkspace}>
              Vincular workspace
            </button>
          </div>
        </div>

        <div className="mgmt-table-wrap">
          <table className="mgmt-table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Papel</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {selectedUserLinks.map(link => (
                <tr key={link.id}>
                  <td>{workspaceNameById[link.workspace_id] || `Workspace ${link.workspace_id}`}</td>
                  <td>{roleLabelById[link.role_id] || `ROLE ${link.role_id}`}</td>
                  <td>{link.ativo ? "Ativo" : "Inativo"}</td>
                  <td className="mgmt-row-actions">
                    <button className="mgmt-link-button danger" type="button" onClick={() => handleUnlinkWorkspace(link.id)}>
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
              {selectedLinkUserId && selectedUserLinks.length === 0 ? (
                <tr>
                  <td colSpan="4">Este usuário ainda não possui workspaces vinculados.</td>
                </tr>
              ) : null}
              {!selectedLinkUserId ? (
                <tr>
                  <td colSpan="4">Selecione um usuário para visualizar os vínculos.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mgmt-card">
        <h2 className="mgmt-section-title">Usuários cadastrados</h2>
        {error ? <p className="mgmt-error">{error}</p> : null}
        <div className="mgmt-table-wrap">
          <table className="mgmt-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Tipo</th>
                <th>Ativo</th>
                <th>Workspaces</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.nome}</td>
                  <td>{user.email}</td>
                  <td>{user.tipo_id === 1 ? "Global" : "Workspace"}</td>
                  <td>{user.ativo ? "Sim" : "Não"}</td>
                  <td>{(userLinksMap[String(user.id)] || []).length}</td>
                  <td className="mgmt-row-actions">
                    <button className="mgmt-link-button" type="button" onClick={() => handleEdit(user)}>Editar</button>
                    <button className="mgmt-link-button" type="button" onClick={() => setSelectedLinkUserId(String(user.id))}>Workspaces</button>
                    <button className="mgmt-link-button danger" type="button" onClick={() => handleDelete(user.id)}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

